import { useState } from "react";
import type { CustomPromptTemplate } from "./promptTemplates";

const STORAGE_KEY = "sandbox_custom_templates";

function loadTemplates(): CustomPromptTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persist(templates: CustomPromptTemplate[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

export default function useCustomTemplates() {
  const [customTemplates, setCustomTemplates] = useState<CustomPromptTemplate[]>(loadTemplates);

  const addTemplate = (t: Omit<CustomPromptTemplate, "id" | "createdAt">) => {
    const newTemplate: CustomPromptTemplate = {
      ...t,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
    };
    setCustomTemplates((prev) => {
      const next = [...prev, newTemplate];
      persist(next);
      return next;
    });
  };

  const updateTemplate = (id: string, updates: Partial<Omit<CustomPromptTemplate, "id" | "createdAt">>) => {
    setCustomTemplates((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, ...updates } : t));
      persist(next);
      return next;
    });
  };

  const deleteTemplate = (id: string) => {
    setCustomTemplates((prev) => {
      const next = prev.filter((t) => t.id !== id);
      persist(next);
      return next;
    });
  };

  return { customTemplates, addTemplate, updateTemplate, deleteTemplate };
}
